interface UndoToastOptions {
    message: string;
    durationMs?: number;
    onUndo: () => void;
    onComplete: () => void;
}

type UndoToastController = { cancel: () => void; finalize: () => void };

const activeUndoToasts = new Set<UndoToastController>();
const TOAST_EXIT_MS = 320;

function revealToast(el: HTMLElement): void {
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            el.classList.add("toast--visible");
        });
    });
}

function dismissToast(el: HTMLElement, onDone?: () => void): void {
    if (el.classList.contains("toast--exiting")) return;

    el.classList.remove("toast--visible");
    el.classList.add("toast--exiting");

    let settled = false;
    const finish = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener("transitionend", onTransitionEnd);
        el.remove();
        onDone?.();
    };

    const onTransitionEnd = (e: TransitionEvent) => {
        if (e.target === el && e.propertyName === "opacity") finish();
    };

    el.addEventListener("transitionend", onTransitionEnd);
    window.setTimeout(finish, TOAST_EXIT_MS + 40);
}

function freezeProgress(progress: HTMLElement): void {
    progress.style.transition = "none";
    const scale =
        progress.getBoundingClientRect().width /
        progress.parentElement!.getBoundingClientRect().width;
    progress.style.transform = `scaleX(${Number.isFinite(scale) ? scale : 0})`;
}

function startProgressTimer(progress: HTMLElement, durationMs: number): void {
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            progress.style.transition = `transform ${durationMs}ms linear`;
            progress.style.transform = "scaleX(0)";
        });
    });
}

export function showUndoToast(options: UndoToastOptions): UndoToastController {
    const durationMs = options.durationMs ?? 5000;
    const container = document.getElementById("toast-container")!;
    const el = document.createElement("div");
    el.className = "toast";

    const progress = document.createElement("div");
    progress.className = "toast-progress";
    progress.setAttribute("aria-hidden", "true");

    el.innerHTML = `
    <span class="toast-message">${escapeHtml(options.message)}</span>
    <button type="button" class="btn-undo">Undo</button>
  `;
    el.appendChild(progress);
    container.appendChild(el);
    revealToast(el);
    startProgressTimer(progress, durationMs);

    let completed = false;
    let timer: ReturnType<typeof setTimeout>;
    let controller: UndoToastController;

    function cancel(): void {
        if (completed) return;
        completed = true;
        clearTimeout(timer);
        activeUndoToasts.delete(controller);
        freezeProgress(progress);
        dismissToast(el, () => options.onUndo());
    }

    function finalize(): void {
        if (completed) return;
        completed = true;
        clearTimeout(timer);
        activeUndoToasts.delete(controller);
        freezeProgress(progress);
        dismissToast(el, () => options.onComplete());
    }

    controller = { cancel, finalize };
    activeUndoToasts.add(controller);

    const undoBtn = el.querySelector(".btn-undo") as HTMLButtonElement;
    undoBtn.addEventListener("click", (e) => {
        e.preventDefault();
        cancel();
    });

    timer = setTimeout(finalize, durationMs);

    return controller;
}

export function finalizePendingToast(): void {
    for (const toast of [...activeUndoToasts]) {
        toast.finalize();
    }
}

export function showToast(message: string, durationMs = 4000): void {
    const container = document.getElementById("toast-container")!;
    const el = document.createElement("div");
    el.className = "toast toast--notice";

    el.innerHTML = `<span class="toast-message">${escapeHtml(message)}</span>`;
    container.appendChild(el);
    revealToast(el);

    window.setTimeout(() => dismissToast(el), durationMs);
}

function escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
}
